import React, { useState, useEffect } from 'react';

import { FileText, ArrowRight } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';

const Resources = () => {
    const [resources, setResources] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchResources = async () => {
            try {
                const q = query(collection(db, 'cases'), orderBy('date_published', 'desc'));
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setResources(data);
            } catch (error) {
                console.error("Error fetching resources:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchResources();
    }, []);

    return (
        <div className="pt-20">
            <SEO
                title="BPO Resources | Case Studies, Blog, Calculators"
                description="Access free BPO resources including ROI calculators, case studies, industry glossaries, and expert insights on outsourcing."
            />

            {/* Hero */}
            <div className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8 border-b border-gray-200">
                <div className="max-w-7xl mx-auto text-center">
                    <FadeIn>
                        <h1 className="text-4xl font-bold text-[#20222D] mb-4">Resources & Case Studies</h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Explore our library of whitepapers, success stories, and tools to help you navigate the world of outsourcing.
                        </p>
                    </FadeIn>
                </div>
            </div>

            <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
                {isLoading ? (
                    <div className="text-center py-20 text-gray-500">Loading resources...</div>
                ) : resources.length === 0 ? (
                    <div className="text-center py-20 bg-gray-50 rounded-2xl border border-gray-100">
                        <p className="text-xl font-bold text-gray-600 mb-2">No resources available yet.</p>
                        <p className="text-gray-500">Our team is working on new case studies. Check back soon.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {resources.map((res, i) => (
                            <FadeIn key={res.id} delay={i * 0.1}>
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-all overflow-hidden h-full flex flex-col">
                                    <div className="h-48 bg-gray-200 relative">
                                        {res.image ? (
                                            <img src={res.image} alt={res.title} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                                                <FileText size={48} />
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4">
                                            <span className="bg-white/90 backdrop-blur-sm text-[#20222D] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                                {res.category || 'Resource'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-6 flex-1 flex flex-col">
                                        <div className="text-xs text-brand-primary mb-2 font-medium">{res.date_published}</div>
                                        <h3 className="text-xl font-bold text-[#20222D] mb-3 line-clamp-2">{res.title}</h3>
                                        <p className="text-gray-600 mb-6 flex-1 line-clamp-3">{res.summary}</p>

                                        {res.file_url ? (
                                            <a
                                                href={res.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-brand-primary font-bold hover:text-brand-accent transition-colors"
                                            >
                                                Download / View <ArrowRight size={16} className="ml-2" />
                                            </a>
                                        ) : (
                                            <span className="text-gray-400 text-sm">Coming Soon</span>
                                        )}
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
export default Resources;
